import React, { useState } from "react";
import styled from "styled-components";
import Swiper from "react-id-swiper";
import { device } from "../../helpers/responsive";
import { X, ChevronLeft, ChevronRight } from "react-feather";
import { InAppAdsCard } from "./InAppAdsCard";

export interface CategoryIcons {
  id: number;
  name: string;
  icon: string;
}
export interface AdCard {
  id: string;
  title: string;
  description: string;
  fullContent?: string;
  image?: string;
  url?: string;
  lat?: string;
  lng?: string;
  region?: string;
  category?: string;
  categoryIcons?: CategoryIcons[];
  activity?: string;
  regionId?: string;
}

interface VisitIdahoDetails {
  id: number;
  title: {
    rendered: string;
  };
  content: {
    rendered: string;
  };
  excerpt: {
    rendered: string;
  };
  link: string;
  adventure_category: number[];
  region: number[];
  season: number[];
  budget: number[];
  activity_level: number[];
}

interface Props {
  ads: AdCard[];
  title?: string;
  showingAds: (state: boolean) => void;
  loading?: boolean;
}

const AdContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 16px;
  right: 16px;
  max-width: 960px;
  margin: 0 auto;
  background: #ffffff;
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 50;
  padding: 16px 20px;
  width: calc(100% - 32px);
  left: 50%;
  transform: translateX(-50%);
  transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
  border-top: 3px solid #c08267;

  ${device.mobile} {
    padding: 20px 24px;
    border-radius: 24px 24px 0px 0px;
    width: calc(100% - 48px);
  }

  ${device.desktop} {
    padding: 24px 28px;
    border-radius: 28px 28px 0px 0px;
    width: calc(100% - 64px);
  }
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 16px 0;
  color: #c08267;
  padding: 0;
  font-family: Albra, sans-serif;
  position: relative;
  display: inline-block;

  &:after {
    content: "";
    position: absolute;
    bottom: -6px;
    left: 0;
    width: 40px;
    height: 3px;
    background: #e6a27c;
  }

  ${device.mobile} {
    font-size: 18px;
    margin: 0 0 20px 0;
  }

  ${device.desktop} {
    font-size: 22px;
    margin: 0 0 22px 0;
  }
`;

const DialogOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  backdrop-filter: blur(3px);
  transition: opacity 0.3s ease;
`;

const Dialog = styled.div`
  background: #f5f5f0;
  border-radius: 28px;
  box-shadow: 0px 12px 24px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 550px;
  max-height: 90vh;
  position: relative;
  overflow: hidden;
  animation: dialogFadeIn 0.3s ease-out;

  @keyframes dialogFadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  ${device.tabletSmAsMax} {
    border-radius: 24px;
    width: 95%;
  }
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 260px;
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Section = styled.div`
  padding: 28px 32px;
  background: #f9f7f4;

  h3 {
    font-size: 18px;
    margin: 24px 0 16px;
    font-weight: 600;
    font-family: Albra, sans-serif;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  left: 16px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  color: #333;
  z-index: 10;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  &:hover {
    background: #ffffff;
  }

  ${device.tabletSmAsMax} {
    top: 16px;
    left: 16px;
  }
`;

const DialogContent = styled.div`
  max-height: calc(85vh - 280px);
  overflow-y: auto;
  background: #f9f7f4;
  font-family: europa, sans-serif;
  padding-bottom: 16px;

  h2 {
    font-size: 24px;
    font-weight: 600;
    margin: 0 0 20px;
    color: #333;
    font-family: Albra, sans-serif;
  }

  img {
    max-width: 100%;
    border-radius: 12px;
    margin-bottom: 24px;
  }

  p {
    font-size: 16px;
    line-height: 1.6;
    color: #555;
    margin: 0 0 20px;
    font-family: europa, sans-serif;
  }

  ul,
  ol {
    margin: 0 0 20px;
    padding-left: 24px;
  }

  li {
    margin-bottom: 8px;
  }
`;

const CloseAdButton = styled.button`
  position: absolute;
  top: -20px;
  right: 20px;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: white;
  border: none;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;

  &:hover {
    transform: rotate(90deg);
  }
`;

const LeftArrow = styled.button`
  left: -12px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  box-shadow: 0px 3px 8px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: all 0.2s ease;
  color: #333;

  &:hover {
    background: #ffffff;
    transform: translateY(-50%) scale(1.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${device.tabletSmAsMax} {
    width: 36px;
    height: 36px;
  }
`;

const RightArrow = styled.button`
  right: -12px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  box-shadow: 0px 3px 8px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: all 0.2s ease;
  color: #333;

  &:hover {
    background: #ffffff;
    transform: translateY(-50%) scale(1.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${device.tabletSmAsMax} {
    width: 36px;
    height: 36px;
  }
`;

const ExploreButton = styled.button`
  position: fixed;
  bottom: 30px;
  left: 50%;
  transform: translateX(calc(480px - 50%));
  padding: 14px 24px;
  background: #c08267;
  border-radius: 24px;
  border: none;
  box-shadow: 0 4px 12px rgba(192, 130, 103, 0.3);
  font-weight: 600;
  cursor: pointer;
  z-index: 49;
  transition: all 0.2s;
  color: white;
  font-family: Albra, sans-serif;
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    stroke-width: 2.5px;
  }

  @media (max-width: 1020px) {
    right: 20px;
    left: auto;
    transform: none;
  }

  &:hover {
    background: #e6a27c;
    @media (min-width: 1021px) {
      transform: translateX(calc(480px - 50%)) translateY(-2px);
    }
    @media (max-width: 1020px) {
      transform: translateY(-2px);
    }
  }
`;

const SwiperContainer = styled.div`
  margin-top: ${props => (props.title ? "16px" : "0")};
  overflow: hidden;
  position: relative;

  .swiper-container {
    overflow: visible;
    padding-bottom: 12px;
  }

  .swiper-wrapper {
    align-items: stretch;
  }

  .swiper-slide {
    height: 100%;
    display: flex;
  }
`;

const ActionButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 18px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 24px;
  background: #b8562e;
  text-decoration: none;
  text-align: center;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;

  &:hover {
    background: #b17055;
  }
`;

const LoadingIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px;

  &:after {
    content: "";
    width: 32px;
    height: 32px;
    border: 3px solid #e0e0e0;
    border-radius: 50%;
    border-top-color: #c08267;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const GlobalStyle = styled.div`
  .swiper-container.full-height,
  .swiper-container.full-height .swiper-wrapper,
  .swiper-container.full-height .swiper-slide {
    height: 100%;
  }
`;

const InAppAds: React.FC<Props> = ({ ads, showingAds, title, loading }) => {
  const [swiper, setSwiper] = useState<any>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const handleSwiperInit = (swiperInstance: any) => {
    if (swiperInstance) {
      setSwiper(swiperInstance);
      setIsBeginning(true);
      setIsEnd(
        swiperInstance.slides.length <= swiperInstance.params.slidesPerView
      );
    }
  };

  const params = {
    slidesPerView: 3,
    freeMode: true,
    spaceBetween: 16,
    containerClass: "swiper-container full-height",
    height: "100%",
    autoHeight: true,
    breakpoints: {
      320: {
        slidesPerView: 2,
        spaceBetween: 16,
      },
      480: {
        slidesPerView: 4,
        spaceBetween: 16,
      },
      768: {
        slidesPerView: 4,
        spaceBetween: 16,
      },
      1024: {
        slidesPerView: 4,
        spaceBetween: 16,
      },
    },
  };

  const [selectedAd, setSelectedAd] = useState<AdCard | null>(null);
  const [isVisible, setIsVisible] = useState(true);
  const [adDetails, setAdDetails] = useState<VisitIdahoDetails | null>(null);
  const [isLoading, setIsLoading] = useState(loading);

  const goNext = () => {
    if (swiper !== null) {
      swiper.slideNext();

      // We need to update these after a slight delay to ensure the transition is complete
      setTimeout(() => {
        if (swiper) {
          setIsBeginning(swiper.isBeginning);
          setIsEnd(swiper.isEnd);
        }
      }, 300);
    }
  };

  const goPrev = () => {
    if (swiper !== null) {
      swiper.slidePrev();

      // We need to update these after a slight delay to ensure the transition is complete
      setTimeout(() => {
        if (swiper) {
          setIsBeginning(swiper.isBeginning);
          setIsEnd(swiper.isEnd);
        }
      }, 300);
    }
  };

  const capitalizeWords = (str: string) => {
    return str.replace(/\b\w/g, match => match.toUpperCase());
  };

  const fetchAdDetails = async (id: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `https://visitidaho.org/wp-json/wp/v2/adventure/${id}`
      );
      const data = await response.json();
      setAdDetails(data);
    } catch (error) {
      console.error("Error fetching ad details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdClick = async (adId: string) => {
    const ad = ads.find(a => a.id === adId);
    setSelectedAd(ad || null);
    if (ad) {
      await fetchAdDetails(adId);
    }
  };

  const handleClose = () => {
    setSelectedAd(null);
    setAdDetails(null);
  };

  return (
    <GlobalStyle>
      {isVisible ? (
        <AdContainer>
          <CloseAdButton
            onClick={() => {
              showingAds(false);
              setIsVisible(false);
            }}
            aria-label="Close recommendations"
          >
            <X size={20} />
          </CloseAdButton>
          {title && <Title>{title}</Title>}
          <SwiperContainer title={title}>
            <Swiper {...params} getSwiper={handleSwiperInit} shouldSwiperUpdate>
              {ads.map(ad => (
                <div key={ad.id} style={{ height: "100%" }}>
                  <InAppAdsCard
                    onClick={() => handleAdClick(ad.id)}
                    iconSrc={
                      ad?.image ||
                      (ad?.categoryIcons && ad?.categoryIcons[0]?.icon)
                    }
                    title={ad.title}
                    paragraph={ad.description}
                    style={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                    }}
                  />
                </div>
              ))}
            </Swiper>
            <LeftArrow
              onClick={goPrev}
              disabled={isBeginning}
              aria-label="Previous slide"
            >
              <ChevronLeft size={20} />
            </LeftArrow>
            <RightArrow
              onClick={goNext}
              disabled={isEnd}
              aria-label="Next slide"
            >
              <ChevronRight size={20} />
            </RightArrow>
          </SwiperContainer>
        </AdContainer>
      ) : (
        <ExploreButton
          onClick={() => {
            showingAds(true);
            setIsVisible(true);
          }}
        >
          Explore More
        </ExploreButton>
      )}

      {selectedAd && (
        <DialogOverlay
          onClick={e => e.target === e.currentTarget && handleClose()}
        >
          <Dialog>
            <CloseButton onClick={handleClose} aria-label="Close details">
              <X size={20} />
            </CloseButton>

            <ImageContainer>
              <img
                src={
                  selectedAd.image ??
                  "https://viiision.com/_assets/media/0b63ac034d5748d4a13a3c3710aaf1ee.jpg"
                }
                alt={selectedAd.title}
              />
            </ImageContainer>

            <DialogContent>
              {isLoading ? (
                <LoadingIndicator />
              ) : (
                <Section>
                  <h2>{selectedAd.title}</h2>

                  {adDetails?.adventure_category &&
                    adDetails.adventure_category.length > 0 && (
                      <>
                        <h3>Activities</h3>

                        <div
                          style={{
                            display: "flex",
                            gap: "10px",
                            marginBottom: "20px",
                          }}
                        >
                          {selectedAd?.categoryIcons &&
                            selectedAd.categoryIcons.map((icon, index) => (
                              <div
                                key={index}
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  padding: "1px",
                                  borderRadius: "50%",
                                  backgroundColor: "#b57251",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  overflow: "hidden",
                                }}
                                title={icon.name}
                              >
                                <img
                                  src={icon.icon}
                                  alt="Activity icon"
                                  style={{
                                    objectFit: "fill",
                                    margin: "0px",
                                    padding: "10px",
                                  }}
                                />
                              </div>
                            ))}
                        </div>
                      </>
                    )}

                  {adDetails?.region && adDetails.region.length > 0 && (
                    <>
                      <h3>Region</h3>
                      <a
                        href="#"
                        style={{
                          color: "#3b696a",
                          textDecoration: "none",
                          display: "block",
                          marginBottom: "20px",
                        }}
                      >
                        {selectedAd.region &&
                          capitalizeWords(selectedAd.region)}
                      </a>
                    </>
                  )}

                  <div
                    dangerouslySetInnerHTML={{
                      __html:
                        adDetails?.content.rendered ||
                        selectedAd.fullContent ||
                        "",
                    }}
                  />

                  <ActionButton
                    href={`https://www.google.com/maps/dir/?api=1&destination=${selectedAd.lat},${selectedAd.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Get Directions
                  </ActionButton>
                </Section>
              )}
            </DialogContent>
          </Dialog>
        </DialogOverlay>
      )}
    </GlobalStyle>
  );
};

export default InAppAds;
